import React, { useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EDITOR_IMAGE_MAX_BYTES, EDITOR_IMAGE_MAX_MB } from '../constants/uploadLimits';
import ConfirmPopup from './ConfirmPopup';
import './RichTextEditor.css';

const apiBase = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const editorUploadImageUrl = `${apiBase}/auth/editor-upload-image/`;

/**
 * Editor de texto enriquecido (CKEditor). El contenido se guarda en HTML.
 * allowImageUpload: si true, muestra botón "Insertar imagen" que sube a nuestro API (el build classic no incluye adapter).
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Escriba aquí...',
  disabled = false,
  minHeight = '120px',
  allowImageUpload = false,
  /** Llamado con la instancia del editor al estar listo (p. ej. para insertar texto en el cursor). */
  onEditorReady,
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');

  const toolbarItems = [
    'undo', 'redo',
    '|',
    'heading',
    '|',
    'bold', 'italic',
    '|',
    'link',
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    '|',
    'numberedList', 'bulletedList',
    '|',
    'indent', 'outdent',
  ];

  const config = {
    placeholder,
    toolbar: {
      items: toolbarItems,
      shouldNotGroupWhenFull: true,
    },
  };

  const handleInsertImageClick = () => {
    if (disabled || !allowImageUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editorRef.current) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Seleccione un archivo de imagen (JPEG, PNG, GIF o WebP).');
      return;
    }
    if (file.size > EDITOR_IMAGE_MAX_BYTES) {
      setUploadError(`La imagen no puede superar ${EDITOR_IMAGE_MAX_MB} MB.`);
      return;
    }
    const formData = new FormData();
    formData.append('upload', file);
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
    try {
      const res = await fetch(editorUploadImageUrl, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Token ${token}` } : {}) },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir la imagen');
      const url = data.url;
      if (url && editorRef.current) {
        const editor = editorRef.current;
        if (editor.commands.get('insertImage')) {
          editor.execute('insertImage', { source: url });
        } else {
          editor.model.change((writer) => {
            const imageElement = writer.createElement('image', { src: url });
            editor.model.insertContent(imageElement, editor.model.document.selection);
          });
        }
      }
    } catch (err) {
      console.error(err);
      setUploadError(err.message || 'No se pudo subir la imagen.');
    }
  };

  return (
    <div className="rich-text-editor-wrap" style={{ minHeight }}>
      <ConfirmPopup
        open={Boolean(uploadError)}
        title="Error"
        message={uploadError}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setUploadError('')}
        onCancel={() => setUploadError('')}
        variant="danger"
      />
      {allowImageUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="rich-text-editor-insert-image"
            onClick={handleInsertImageClick}
            disabled={disabled}
            title="Insertar imagen"
          >
            Insertar imagen
          </button>
        </>
      )}
      <CKEditor
        editor={ClassicEditor}
        data={value || ''}
        config={config}
        disabled={disabled}
        onReady={(editor) => {
          editorRef.current = editor;
          if (typeof onEditorReady === 'function') onEditorReady(editor);
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          if (typeof onChange === 'function') onChange(data);
        }}
      />
    </div>
  );
};

export default RichTextEditor;
